import { create } from 'zustand';

/**
 * Timer Progress interface - real-time data from socket events
 */
export interface TimerProgress {
  appointmentId: string;
  elapsed: number;
  remaining: number;
  total: number;
  progress: number;
  isCurrentlyActive: boolean;
  isTracking: boolean;
  trackingAppointmentId?: string;
  yourPosition?: number;
  lateness?: {
    actualStart: string;
    isLate: boolean;
    lateByMinutes: number;
    lateBySeconds: number;
    scheduledStart: string;
  };
}

/**
 * Active Timer state - tracks which appointment timer is running
 */
export interface ActiveTimer {
  appointmentId: string;
  startTime: number;
  timestamp: string;
}

export interface TimerState {
  // State
  activeTimer: ActiveTimer | null;
  timerProgress: TimerProgress | null;

  // Actions
  setActiveTimer: (timer: ActiveTimer | null) => void;
  setTimerProgress: (progress: TimerProgress | null) => void;
  clearTimer: () => void;
}

/**
 * Timer Store - Real-time timer state from Socket.io events
 *
 * Purpose: Store ephemeral timer data received via socket events
 * NOT for: REST API data (use React Query)
 *
 * Data sources:
 * - timer:started event → activeTimer
 * - timer:progress event → timerProgress
 * - timer:stopped event → clearTimer()
 * - timer:recovered event → activeTimer (for page refresh recovery)
 */
export const useTimerStore = create<TimerState>(set => ({
  // Initial state
  activeTimer: null,
  timerProgress: null,

  // Actions
  setActiveTimer: timer => set({ activeTimer: timer }),

  setTimerProgress: progress => set({ timerProgress: progress }),

  clearTimer: () =>
    set({
      activeTimer: null,
      timerProgress: null,
    }),
}));
